<template>
  <div
    v-if="isNotMobile || showFooter"
    class="i-select-plan-footer"
  >
    <div class="cta">
      <div class="details">
        <div class="label">
          <template v-if="currentPlan?.subtitle && !props.addonSelected">
            <span class="plan-title">{{ currentPlan.title }}</span>
            <span class="plan-separator"> - </span>
            <span class="plan-subtitle">{{ currentPlan.subtitle }}</span>
          </template>
          <template v-else>
            {{ currentPlan?.title }}
            <span v-if="props.addonSelected"> + Resume Package</span>
          </template>
        </div>
        <div class="price">
          <span
            v-if="!!currentPlan?.discounted || props.addonSelected"
            class="slashed"
          >
            ${{
              props.addonSelected
                ? (parseFloat(currentPlan?.discounted || currentPlan?.price || "0") + 499.9).toFixed(2)
                : currentPlan?.discounted
            }}
          </span>
          <span class="total">
            ${{ props.addonSelected ? (parseFloat(currentPlan?.price || "0") + 299.9).toFixed(2) : currentPlan?.price }}
          </span>
          <span v-if="currentPlan?.renews">then {{ currentPlan?.renews }}</span
          ><span v-if="currentPlan?.isSubscription">/month</span>
        </div>
      </div>
      <iBaseButton
        class="button"
        v-bind="buttonStyle"
        :is-disabled="isPaymentButtonDisabled"
        :text="currentPlan?.isSubscription ? 'Subscribe & Save' : 'Reserve Your Spot'"
        @clicked="props.disablePaymentUntilBooking ? emit('trigger-booking') : firePaymentAction()"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch, type Ref } from "vue"

import useIsScreenSize from "@/composables/useIsScreenSize"

// COMPONENTS
import iBaseButton from "@/components/base/BaseButton.vue"

// TYPES
import StripePrice from "@/shared/enums/StripePrice"
import type { CoachingPlans } from "@/types"

// STYLES
import { bluePurpleGradient, bluePurpleGradientLight, white } from "@/styles/colors"
import { breakpointSm } from "@/styles/variables"
import getPaymentAction from "@/composables/getPaymentAction"
import { UserFlow } from "@/services/gtmService"

// EMITS
const emit = defineEmits(["trigger-booking"])

// COMPOSABLES
const isNotMobile = useIsScreenSize(breakpointSm)

// REFS
const isPaymentButtonDisabled = ref(false)
const planSection: Ref<HTMLElement | null> = ref(null)
const showFooter = ref(false)

const props = defineProps({
  coachingPlans: {
    type: Object as () => CoachingPlans,
    required: true
  },
  chosenPlan: {
    type: String as () => StripePrice,
    required: true
  },
  disablePaymentUntilBooking: {
    type: Boolean,
    default: false
  },
  checkoutTrigerred: {
    type: Boolean,
    default: false
  },
  addonSelected: {
    type: Boolean,
    required: true
  },
  discountIdentifier: {
    type: String,
    default: null
  }
})

// COMPUTED
const currentPlan = computed(() => props.coachingPlans[props.chosenPlan])
const buttonStyle = computed(() => {
  return {
    backgroundImage: isPaymentButtonDisabled.value ? bluePurpleGradientLight : bluePurpleGradient,
    borderRadius: "8px",
    fontWeight: "500",
    textColor: white,
    width: isNotMobile.value ? "max-content" : "100%",
    padding: isNotMobile.value ? "8px 70px" : "10px 6px",
    size: isNotMobile.value ? "medium" : "small"
  }
})

// FUNCTIONS
const firePaymentAction = async () => {
  disablePaymentButton()

  const coachingPlan = currentPlan.value
  if (coachingPlan) {
    const stripePrices = [coachingPlan.stripePrice]
    if (props.addonSelected) stripePrices.push(StripePrice.RESUME_COACHING_ADDON)

    const paymentAction = getPaymentAction(
      stripePrices,
      UserFlow.COACHING,
      enablePaymentButton,
      props.discountIdentifier
    )
    try {
      await paymentAction()
    } catch (error) {
      isPaymentButtonDisabled.value = false
    }
  }
}

const disablePaymentButton = () => {
  isPaymentButtonDisabled.value = true
}

const enablePaymentButton = () => {
  isPaymentButtonDisabled.value = false
}

const observePlanSection = () => {
  planSection.value = document.querySelector("#plan-section")
  if (!planSection.value) return

  const observer = new IntersectionObserver((entries, observer) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        showFooter.value = true
        observer.unobserve(entry.target) // Stop observing after the first trigger
      }
    })
  })
  observer.observe(planSection.value)
}

// LIFECYCLE
onMounted(() => {
  observePlanSection()
  enablePaymentButton()
})

// WATCHERS
watch(
  () => props.checkoutTrigerred,
  async (newValue) => {
    if (newValue) await firePaymentAction()
  }
)
</script>

<style lang="scss" scoped>
.i-select-plan-footer {
  background-color: $white;
  box-shadow:
    0 -5px 10px 0 rgb(83 83 83 / 7%),
    0 5px 10px 0 rgb(83 83 83 / 7%);
  margin: 0;
  padding: 0 20px;
  position: sticky;
  bottom: 0;
  animation: fade-in 1.5s ease 0s 1 normal forwards;

  @media screen and (min-width: $breakpoint-md) {
    width: 100%;
    animation: unset;

    @include border-radius-none;
  }

  @keyframes fade-in {
    0% {
      opacity: 0;
      bottom: -70px;
    }

    100% {
      opacity: 1;
      bottom: 0;
    }
  }

  .cta {
    @include flex(column, start, center, $gap: 5px);

    width: 100%;
    max-width: 1200px;
    padding: 10px 0;
    margin: auto;

    @media screen and (min-width: $breakpoint-md) {
      @include flex(row, end, center, $gap: 5px);

      padding: 8px 0;
      gap: 25px;
      width: unset;
    }

    .details {
      @include flex(column, start, center, $gap: 3px);
      @include text-sm;

      color: $comet500;
      text-align: center;

      @media screen and (min-width: $breakpoint-md) {
        align-items: end;
        text-align: start;
      }

      .label {
        font-weight: 500;

        .plan-title {
          font-weight: 600;
        }

        .plan-separator {
          color: $comet400;
        }

        .plan-subtitle {
          font-weight: 300;
          color: $comet400;
        }
      }

      .price {
        @include flex(row, start, center, $gap: 3px);

        width: max-content;

        .slashed {
          @include text-xl;

          font-weight: 500;
          text-decoration: line-through;
          margin-right: 5px;
        }

        .total {
          @include text-xl;

          color: $black950;
          font-weight: 700;
        }
      }
    }
  }

  .modal {
    .top {
      @include flex($direction: column, $justify: start, $align: center, $gap: 17px);
      @include text-md;

      margin: 25px;

      @media screen and (min-width: $breakpoint-md) {
        margin: 35px;

        @include text-lg;
      }

      .caption {
        font-weight: 700;
        text-align: center;

        @include display-xs;

        @media screen and (min-width: $breakpoint-md) {
          @include display-sm;
        }
      }
    }

    .bottom {
      @include flex($direction: row, $justify: center, $align: center, $gap: 0);

      background-color: $black50;
      padding: 12px 25px;
      border-bottom-left-radius: 16px;
      border-bottom-right-radius: 16px;
    }
  }
}
</style>
